import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { levelDbService } from '../services/levelDbService';
import { gameService } from '../services/gameService';

interface GameCompletedScreenProps {
  // Props will be determined by URL params
}

const GameCompletedScreen: React.FC<GameCompletedScreenProps> = () => {
  const { id, sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameData, setGameData] = useState<{
    currentLevel: number;
    highestLevel: number;
    rescuedEmojis: Record<string, number>;
    themeName?: string;
    themeDescription?: string;
    isWin: boolean;
    playerName?: string;
  } | null>(null);

  useEffect(() => {
    const loadCompletedGameData = async () => {
      try {
        setLoading(true);
        
        // Determine if it's auto mode (session-based) or user-created game
        const isAutoMode = id === 'auto' || !!sessionId;
        const actualGameId = isAutoMode ? sessionId : id;

        if (!actualGameId) {
          setError('Invalid game ID');
          return;
        }

        if (isAutoMode) {
          // Load auto mode session data
          const session = await levelDbService.getLevelSession(actualGameId);
          if (!session) {
            setError('Game session not found');
            return;
          }

                     setGameData({
             currentLevel: session.currentLevel,
             highestLevel: session.highestLevel,
             rescuedEmojis: session.rescuedEmojis || {},
             isWin: session.status === 'won',
             playerName: session.gameName
           });
        } else {
          // Load user-created game data
          const gameData = await gameService.loadGameData(actualGameId, { width: 800, height: 600 });
          if (!gameData) {
            setError('Game not found');
            return;
          }

          // For user-created games, we'll show basic completion info
          setGameData({
            currentLevel: 1,
            highestLevel: 1,
            rescuedEmojis: {}, // User games don't have emoji collection
            isWin: true, // Assume completion if they're viewing this
                         playerName: gameData.playerName || undefined
          });
        }
      } catch (error) {
        console.error('Error loading completed game data:', error);
        setError('Failed to load game data');
      } finally {
        setLoading(false);
      }
    };

    loadCompletedGameData();
  }, [id, sessionId]);

  const totalRescued = gameData ? Object.values(gameData.rescuedEmojis).reduce((sum, count) => sum + count, 0) : 0;
  const sortedEmojis = gameData ? Object.entries(gameData.rescuedEmojis).sort(([,a], [,b]) => b - a) : [];

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFC107',
        fontSize: '1.5rem'
      }}>
        Loading achievement data...
      </div>
    );
  }

  if (error || !gameData) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FF5722',
        fontSize: '1.5rem',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '20px' }}>❌ {error || 'Game not found'}</div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '12px 25px',
            fontSize: '1.1rem',
            backgroundColor: '#FFC107',
            color: '#222',
            border: 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          🏠 Go Home
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#1a1a1a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '20px',
        padding: '25px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        overflowY: 'auto',
        textAlign: 'center',
        border: '3px solid #FFC107',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ 
            fontSize: '2rem', 
            color: '#FFC107', 
            margin: '0 0 8px 0',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            🎯 Emojis Freed
          </h1>
          
          {gameData.playerName && (
            <div style={{ 
              fontSize: '1.2rem', 
              color: '#E91E63', 
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              {gameData.playerName}
            </div>
          )}
          
          <div style={{ 
            fontSize: '1.3rem', 
            color: '#E91E63', 
            fontWeight: 'bold',
            marginBottom: '15px'
          }}>
            Total Emojis Rescued: {totalRescued} 🚀
          </div>
        </div>
        
        {/* Emoji Collection - Same badge style as main screen */}
        {sortedEmojis.length > 0 ? (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '20px',
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '10px'
          }}>
            {sortedEmojis.map(([emoji, count]) => (
              <span
                key={emoji}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: '1.4rem',
                  background: 'rgba(255,255,255,0.18)',
                  borderRadius: '999px',
                  padding: '3px 12px 3px 10px',
                  color: '#222',
                  border: '1px solid #FFC107',
                  fontWeight: 600,
                  boxShadow: '0 1px 4px rgba(76,175,80,0.07)',
                  minWidth: 0,
                  lineHeight: 1.1
                }}
              >
                <span style={{ fontSize: '1.5em', marginRight: 5 }}>{emoji}</span>
                <span style={{
                  background: '#FFC107',
                  color: '#222',
                  borderRadius: '999px',
                  fontSize: '0.9em',
                  fontWeight: 700,
                  padding: '0 8px',
                  marginLeft: 3,
                  marginRight: -3,
                  minWidth: 20,
                  textAlign: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                }}>×{count}</span>
              </span>
            ))}
          </div>
        ) : (
          <div style={{
            fontSize: '1.2rem',
            color: '#fff',
            opacity: 0.7,
            marginBottom: '20px',
            padding: '20px'
          }}>
            {gameData.isWin ? '🎉 Mission Accomplished! 🎉' : 'Game completed!'}
          </div>
        )}

        <div style={{
          margin: '10px',
          fontSize: '0.9rem',
          color: '#fff',
          opacity: 0.6
        }}>
          {gameData.currentLevel} Levels Completed!
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
            onClick={() => {
              // Navigate to the next level (current game session)
              const isAutoMode = id === 'auto' || !!sessionId;
              const actualGameId = isAutoMode ? sessionId : id;
              const nextLevelUrl = isAutoMode 
                ? `/game/auto/${actualGameId}`
                : `/game/${actualGameId}`;
              navigate(nextLevelUrl);
            }}
            style={{
              padding: '10px 25px',
              fontSize: '1.1rem',
              backgroundColor: '#E91E63',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            🚀 Next Level
          </button>
          
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '10px 25px',
              fontSize: '1.1rem',
              backgroundColor: '#FFC107',
              color: '#222',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            🏠 Home
          </button>
        </div>
        
        {/* Footer */}
        {/* <div style={{
          marginTop: '20px',
          fontSize: '0.9rem',
          color: '#fff',
          opacity: 0.6
        }}>
          {gameData.currentLevel} Levels Completed!
        </div> */}
      </div>
    </div>
  );
};

export default GameCompletedScreen; 